import React, { useEffect, useState } from 'react'
import { useDashboardContext } from '../../../../contexts/NewDashboardContext';
import { alertV2 } from '../../../../api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import {
  Card,
  CardBody,
  CardExpandToggler,
} from "../../../../components/card/card";
import { useNavigate } from "react-router-dom";
import { startOfMonth, endOfMonth, format } from "date-fns";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export const AlarmTrend = ({}) => {
  const { dateRangeOverview } = useDashboardContext();
  const [chartData, setChartData] = useState(null);
  const navigate = useNavigate();
  // Get theme colors from CSS variables
  const getThemeColors = () => {
    const bodyBg = getComputedStyle(document.body)
      .getPropertyValue("--bs-body-bg")
      .trim();
    const textColor = getComputedStyle(document.body)
      .getPropertyValue("--bs-body-color")
      .trim();
    const gridColor = getComputedStyle(document.body)
      .getPropertyValue("--bs-border-color")
      .trim();
    const themeColor = getComputedStyle(document.body)
      .getPropertyValue("--bs-theme")
      .trim();

    return { bodyBg, textColor, gridColor, themeColor };
  };

  const { bodyBg, textColor, gridColor, themeColor } = getThemeColors();

  const handleChartClick = (event, elements) => {
    if (elements.length > 0) {
      const elementIndex = elements[0].index;
      const month = chartData.labels[elementIndex];
      const year = new Date().getFullYear();

      // First day of month
      const startDate = format(
        startOfMonth(new Date(`${month} 1, ${year}`)),
        "yyyy-MM-dd"
      );

      // Last day of month
      const endDate = format(
        endOfMonth(new Date(`${month} 1, ${year}`)),
        "yyyy-MM-dd"
      );

      navigate(`/alarm/list?start_date=${startDate}&end_date=${endDate}`);
    }
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: handleChartClick,
    onHover: (event, elements) => {
      event.native.target.style.cursor =
        elements.length > 0 ? "pointer" : "default";
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: "Alarm Trend",
        color: textColor,
        font: {
          size: 14,
          weight: "bold",
        },
      },
      tooltip: {
        backgroundColor: bodyBg,
        titleColor: textColor,
        bodyColor: textColor,
        borderColor: gridColor,
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        ticks: {
          color: textColor,
          font: {
            size: 12,
          },
        },
        grid: {
          color: "#4b4b4b",
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "No. Alarm",
          color: textColor,
          font: {
            size: 12,
          },
        },
        ticks: {
          color: textColor,
          font: {
            size: 12,
          },
          stepSize: 1,
        },
        grid: {
          color: "#4b4b4b",
          drawBorder: false,
        },
      },
    },
  };

  async function getData() {
    try {
      const result = await alertV2.listByDate({
        start_date: dateRangeOverview?.startDate,
        end_date: dateRangeOverview?.endDate,
      });
      const body = await result.json();

      // Process the data to create chart data
      processChartData(body.data);
    } catch (error) {
      console.error("Error fetching alarm data:", error);
      // Use mock data for demonstration
      createMockChartData();
    }
  }

  const processChartData = (apiData) => {
    if (!apiData || !Array.isArray(apiData)) {
      console.log("No valid API data, using mock data");
      createMockChartData();
      return;
    }

    // Group alerts by month based on max_date
    const monthlyAlerts = {};

    apiData.forEach((alert) => {
      if (alert.created_at) {
        const date = new Date(alert.created_at);
        const monthKey = `${date.getFullYear()}-${String(
          date.getMonth() + 1
        ).padStart(2, "0")}`;
        const monthName = date.toLocaleDateString("en-US", { month: "long" });

        if (!monthlyAlerts[monthKey]) {
          monthlyAlerts[monthKey] = {
            monthName: monthName,
            count: 0,
          };
        }
        monthlyAlerts[monthKey].count++;
      }
    });

    // Sort by date and get the data
    const sortedMonths = Object.keys(monthlyAlerts).sort();

    const labels = [];
    const data = [];

    sortedMonths.forEach((monthKey) => {
      const monthData = monthlyAlerts[monthKey];
      labels.push(monthData.monthName);
      data.push(monthData.count);
    });

    // If no data, use mock data
    if (labels.length === 0) {
      console.log("No chart data generated, using mock data");
      createMockChartData();
      return;
    }

    const chartData = {
      labels: labels,
      datasets: [
        {
          label: "No. Alarm",
          data: data,
          backgroundColor: themeColor,
          borderWidth: 2,
          borderRadius: 4,
        },
      ],
    };

    setChartData(chartData);
  };

  const createMockChartData = () => {
    const data = {
      labels: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ],
      datasets: [
        {
          label: "No. Alarm",
          data: [],
          backgroundColor: themeColor,
          borderWidth: 2,
          borderRadius: 4,
        },
      ],
    };
    setChartData(data);
  };

  useEffect(() => {
    getData();
  }, [dateRangeOverview]);

  // If no data is available yet, show loading
  if (!chartData) {
    return (
      <div
        style={{
          height: "300px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: bodyBg,
          borderRadius: "8px",
          border: `1px solid ${gridColor}`,
        }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Card>
        <CardBody>
          <div className="d-flex justify-content-end">
            <CardExpandToggler />
          </div>
          <div
            style={{ position: "relative", minHeight: "370px", width: "100%" }}
          >
            <Bar options={options} data={chartData} />
          </div>
        </CardBody>
      </Card>
    </div>
  );
};
